import { apiClient } from "@api/client";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { getActiveSession } from "@auth/sessionStore";
import { type PhoneCall, toPhoneCall } from "@phone/phoneCallsClient";

/**
 * Mirrors brian's `_REFUSAL_STATUS` plus the dispatch and provider outcomes
 * (routes/outbound_calls.py).
 * - 502 `dispatch_rejected`: the call already ended; a fresh key is the retry.
 * - 503 `dispatch_unknown`: the claim is kept; read the call, never re-dial blind.
 */
type OutboundRefusal =
  | "phone_not_configured"
  | "outbound_disabled"
  | "capacity"
  | "line_busy"
  | "endpoint_unknown"
  | "destination_unknown"
  | "endpoint_disabled"
  | "bad_destination"
  | "dispatch_rejected"
  | "dispatch_unknown"
  | "phone_provider_unconfigured";

const REFUSALS: ReadonlySet<string> = new Set<OutboundRefusal>([
  "phone_not_configured",
  "outbound_disabled",
  "capacity",
  "line_busy",
  "endpoint_unknown",
  "destination_unknown",
  "endpoint_disabled",
  "bad_destination",
  "dispatch_rejected",
  "dispatch_unknown",
  "phone_provider_unconfigured",
]);

const isRefusal = (value: unknown): value is OutboundRefusal =>
  typeof value === "string" && REFUSALS.has(value);

/** Extends {@link ClinicAuthError} so the mutation floor still handles it; `phoneCallUuid` is set on dispatch outcomes. */
export class OutboundCallError extends ClinicAuthError {
  readonly refusal: OutboundRefusal;
  readonly phoneCallUuid: string | null;

  constructor(
    refusal: OutboundRefusal,
    status: number,
    phoneCallUuid: string | null,
  ) {
    super("unknown", `HTTP ${status} ${refusal}`, status);
    this.name = "OutboundCallError";
    this.refusal = refusal;
    this.phoneCallUuid = phoneCallUuid;
  }
}

export const describeRefusal = (refusal: OutboundRefusal): string => {
  switch (refusal) {
    case "phone_not_configured":
      return "Brian's phone line isn't set up yet.";
    case "outbound_disabled":
      return "Outbound calling is turned off for this clinic.";
    case "capacity":
      return "All lines are busy right now.";
    case "line_busy":
      return "This line is already on a call.";
    case "endpoint_unknown":
      return "That line isn't one of this clinic's anymore.";
    case "endpoint_disabled":
      return "That line is switched off in Brian's settings.";
    case "bad_destination":
      return "That isn't a number Brian can dial.";
    case "destination_unknown":
      return "That number isn't on file anymore.";
    case "dispatch_rejected":
      return "The call ended before it started.";
    case "dispatch_unknown":
      return "Checking the call…";
    case "phone_provider_unconfigured":
      return "Brian can't place calls from this environment.";
  }
};

export const isRetryable = (refusal: OutboundRefusal): boolean =>
  refusal === "dispatch_rejected" ||
  refusal === "line_busy" ||
  refusal === "capacity";

export type OutboundDestination =
  | { kind: "client_number"; clientPhoneNumberUuid: string }
  | { kind: "number"; e164: string };

const detailOf = (error: unknown): unknown =>
  typeof error === "object" && error !== null && "detail" in error
    ? (error as { detail: unknown }).detail
    : undefined;

/** Bare codes are a string `detail`; dispatch outcomes are `{error, phone_call_uuid}`. */
const refusalFrom = (
  error: unknown,
  status: number,
): OutboundCallError | null => {
  const detail = detailOf(error);
  if (isRefusal(detail)) return new OutboundCallError(detail, status, null);
  if (typeof detail === "object" && detail !== null) {
    const { error: code, phone_call_uuid } = detail as {
      error?: unknown;
      phone_call_uuid?: unknown;
    };
    if (isRefusal(code))
      return new OutboundCallError(
        code,
        status,
        typeof phone_call_uuid === "string" ? phone_call_uuid : null,
      );
  }
  return null;
};

/** One `idempotencyKey` per click: a lost-response retry reuses it, "Try again" mints a new one. */
export const startOutboundCall = async ({
  endpointUuid,
  destination,
  idempotencyKey,
}: {
  endpointUuid: string;
  destination: OutboundDestination;
  idempotencyKey: string;
}): Promise<PhoneCall> => {
  if (!getActiveSession())
    throw new ClinicAuthError("invalid_credentials", "No active session");

  let result;
  try {
    result = await apiClient.POST("/outbound-calls", {
      body: {
        endpoint_uuid: endpointUuid,
        idempotency_key: idempotencyKey,
        ...(destination.kind === "client_number"
          ? { client_phone_number_uuid: destination.clientPhoneNumberUuid }
          : { destination_e164: destination.e164 }),
      },
    });
  } catch {
    throw new ClinicAuthError("network_error");
  }
  if (result.data) return toPhoneCall(result.data);

  const status = result.response.status;
  if (status === 401) throw new ClinicAuthError("invalid_credentials");
  throw (
    refusalFrom(result.error, status) ??
    new ClinicAuthError("unknown", `HTTP ${status}`, status)
  );
};
